<template>
  <PageHeader
    title="Patient Search"
    :breadcrumbs="[{ label: 'Patient' }]"
  />

  <div class="patient-search-container">
    <!-- Search Form -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-6">
      <ComponentHeader title="Search Criteria" />
      <form @submit.prevent="searchPatients" class="p-6 space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Patient Identifier (MBI)</label>
            <input v-model="searchFilters.MBI" type="text" class="sensitive search-input" placeholder="Enter MBI ID" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input v-model="searchFilters.LAST_NAME" type="text" class="sensitive search-input" placeholder="Enter last name" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input v-model="searchFilters.FIRST_NAME" type="text" class="sensitive search-input" placeholder="Enter first name" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
            <input v-model="searchFilters.DATE_OF_BIRTH" type="date" class="sensitive search-input" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Gender</label>
            <select v-model="searchFilters.GENDER" class="search-input">
              <option value="">Select Gender</option>
              <option value="1">Male</option>
              <option value="2">Female</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
            <input v-model="searchFilters.BENE_ZIP_CD" type="text" class="search-input" placeholder="Enter ZIP code" />
          </div>
        </div>

        <div class="pt-4">
          <div class="flex gap-2">
            <button type="submit" :disabled="loading" class="btn-primary">
              <span v-if="!loading" class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
                Search
              </span>
              <span v-else class="flex items-center gap-2">
                <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </span>
            </button>
            <button type="button" @click="clearSearch" class="btn-secondary">Clear</button>
          </div>
          <div v-if="validationError" class="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <span class="text-sm text-red-800 font-medium">{{ validationError }}</span>
          </div>
        </div>
      </form>
    </div>

    <!-- Results -->
    <div v-if="hasSearched" class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
      <ComponentHeader :title="resultsTitle" />

      <!-- Loading -->
      <div v-if="loading" class="p-12">
        <LoadingIndicator message="Searching for patients..." />
      </div>

      <!-- Results Table -->
      <div v-else-if="patients.length > 0" class="results-table-wrap">
        <table class="results-table">
          <thead>
            <tr>
              <th @click="sortBy('LAST_NAME')" class="sortable">
                Last Name <span v-if="sortCol === 'LAST_NAME'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th @click="sortBy('FIRST_NAME')" class="sortable">
                First Name <span v-if="sortCol === 'FIRST_NAME'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th @click="sortBy('MBI')" class="sortable">
                MBI <span v-if="sortCol === 'MBI'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th @click="sortBy('DATE_OF_BIRTH')" class="sortable">
                DOB <span v-if="sortCol === 'DATE_OF_BIRTH'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th>Gender</th>
              <th>Age</th>
              <th @click="sortBy('BENE_ZIP_CD')" class="sortable">
                ZIP <span v-if="sortCol === 'BENE_ZIP_CD'" class="sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
              </th>
              <th>State</th>
              <th>ACO</th>
              <th>Phone</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in sortedPatients" :key="p.MBI" @click="goToPatient(p.MBI)" class="result-row">
              <td><span class="patient-name sensitive">{{ p.LAST_NAME }}</span></td>
              <td class="sensitive">{{ p.FIRST_NAME }}</td>
              <td><span class="mbi-link sensitive">{{ p.MBI }}</span></td>
              <td class="sensitive">{{ p.DATE_OF_BIRTH }}</td>
              <td>{{ formatGender(p.GENDER) }}</td>
              <td>{{ parseInt(p.BENE_AGE) || '' }}</td>
              <td>{{ p.BENE_ZIP_CD }}</td>
              <td>{{ p.BENE_FIPS_STATE_CD }}</td>
              <td>{{ p.ACO_ID }}</td>
              <td class="sensitive">{{ p.PHONE_PRIMARY }}</td>
              <td class="sensitive">{{ p.EMAIL }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Results -->
      <div v-else class="p-12 text-center">
        <p class="text-gray-600">No patients found matching your search criteria.</p>
        <p class="text-sm text-gray-500 mt-2">Try adjusting your search parameters.</p>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
      <span class="text-red-800">{{ error }}</span>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { formatDate } from '@/utils/common'
import PageHeader from '../library/PageHeader.vue'
import ComponentHeader from '../library/ComponentHeader.vue'
import LoadingIndicator from '../library/LoadingIndicator.vue'

export default {
  name: 'PatientSearch',
  components: { PageHeader, ComponentHeader, LoadingIndicator },
  data() {
    return {
      searchFilters: {
        MBI: '',
        LAST_NAME: '',
        FIRST_NAME: '',
        DATE_OF_BIRTH: '',
        GENDER: '',
        BENE_ZIP_CD: '',
      },
      patients: [],
      totalResults: 0,
      searchMs: 0,
      loading: false,
      hasSearched: false,
      error: null,
      validationError: null,
      sortCol: 'LAST_NAME',
      sortDir: 'asc',
    }
  },
  computed: {
    resultsTitle() {
      if (this.patients.length > 0) {
        const shown = this.patients.length
        const total = this.totalResults
        const ms = this.searchMs
        if (total > shown) {
          return `Showing ${shown} of ${total.toLocaleString()} patients (${ms}ms)`
        }
        return `${shown} patient${shown !== 1 ? 's' : ''} found (${ms}ms)`
      }
      return 'Search Results'
    },
    sortedPatients() {
      if (!this.sortCol) return this.patients
      const col = this.sortCol
      const dir = this.sortDir === 'asc' ? 1 : -1
      return [...this.patients].sort((a, b) => {
        const va = (a[col] || '').toString()
        const vb = (b[col] || '').toString()
        return va.localeCompare(vb) * dir
      })
    }
  },
  mounted() {
    const saved = sessionStorage.getItem('patientSearchState')
    if (saved) {
      const state = JSON.parse(saved)
      // In Demo Mode the demoProtectedFields mixin empties the identifying
      // terms in this same tick and rewrites the snapshot without them.
      this.searchFilters = state.searchFilters
      this.patients = state.patients
      this.totalResults = state.totalResults
      this.searchMs = state.searchMs
      this.hasSearched = state.hasSearched
      this.sortCol = state.sortCol || 'LAST_NAME'
      this.sortDir = state.sortDir || 'asc'
    }
  },
  watch: {
    searchFilters: {
      deep: true,
      handler() { if (this.validationError) this.validationError = null }
    }
  },
  methods: {
    saveState() {
      sessionStorage.setItem('patientSearchState', JSON.stringify({
        searchFilters: this.searchFilters,
        patients: this.patients,
        totalResults: this.totalResults,
        searchMs: this.searchMs,
        hasSearched: this.hasSearched,
        sortCol: this.sortCol,
        sortDir: this.sortDir,
      }))
    },
    validateSearch() {
      const f = this.searchFilters
      const hasAny = Object.values(f).some(v => v && v.trim())
      if (!hasAny) {
        this.validationError = 'Please enter at least one search criterion'
        return false
      }
      if ((f.GENDER || f.DATE_OF_BIRTH) && !f.MBI && !f.LAST_NAME && !f.FIRST_NAME && !f.BENE_ZIP_CD) {
        this.validationError = 'Please provide additional search criteria beyond gender or date of birth'
        return false
      }
      this.validationError = null
      return true
    },

    async searchPatients() {
      if (!this.validateSearch()) return
      this.loading = true
      this.error = null
      this.hasSearched = true

      const params = { type: 'PATIENT', limit: 100 }
      for (const [key, value] of Object.entries(this.searchFilters)) {
        if (value && value.trim()) params[key] = value.trim()
      }

      try {
        const { data } = await axios.get('/api/graph/search/', { params })
        this.patients = data.results || []
        this.totalResults = data.total || 0
        this.searchMs = data.ms || 0
        this.saveState()
      } catch (err) {
        this.error = err.response?.data?.error || err.response?.data?.detail || 'Search failed.'
        this.patients = []
      } finally {
        this.loading = false
      }
    },

    clearSearch() {
      this.searchFilters = { MBI: '', LAST_NAME: '', FIRST_NAME: '', DATE_OF_BIRTH: '', GENDER: '', BENE_ZIP_CD: '' }
      this.patients = []
      this.totalResults = 0
      this.searchMs = 0
      this.hasSearched = false
      this.error = null
      this.validationError = null
      sessionStorage.removeItem('patientSearchState')
    },

    sortBy(col) {
      if (this.sortCol === col) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortCol = col
        this.sortDir = 'asc'
      }
    },

    goToPatient(mbi) {
      if (mbi) this.$router.push(`/patients/${mbi}`)
    },

    formatGender(val) {
      return { '1': 'Male', '2': 'Female' }[val] || val || ''
    },
  }
}
</script>

<style scoped>
.patient-search-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
}
.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}
.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.results-table-wrap {
  overflow-x: auto;
}
.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}
.results-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}
.results-table th {
  background: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  padding: 0.625rem 0.75rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  white-space: nowrap;
  user-select: none;
}
.results-table th.sortable {
  cursor: pointer;
}
.results-table th.sortable:hover {
  background: #f3f4f6;
}
.sort-arrow {
  font-size: 0.625rem;
  margin-left: 0.25rem;
  color: #6b7280;
}
.results-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  white-space: nowrap;
}
.result-row {
  cursor: pointer;
  transition: background 0.1s;
}
.result-row:hover {
  background: #f0f7ff;
}
.patient-name {
  font-weight: 500;
}
.mbi-link {
  color: #2563eb;
  font-family: ui-monospace, monospace;
  font-size: 0.8125rem;
}
.result-row:hover .mbi-link {
  text-decoration: underline;
}
</style>
